import axios from "axios";
import dayjs from "../utils/dayjsConfig";
import { attachSentryResponseInterceptor } from "./sentryAxiosInterceptor";

//TYPES
import { AuthData } from "../types/types";
import { genericErrorMessage } from "../utils";

//API BELONGING TO THE OLD PROJECT OF 'API Front Transferencias Web'

//AXIOS INSTANCE

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_TRANSACTIONS_BASE_URL,
  timeout: 100000,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const localAuth = localStorage.getItem("allaria-auth");

    const parsedLocalAuth: AuthData | null =
      (localAuth && JSON.parse(localAuth)) ?? null;

    const token = parsedLocalAuth?.access_token;

    if (token) {
      config.headers["x-web-token"] = token;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
attachSentryResponseInterceptor(axiosInstance);

interface Currency {
  descripcion: string;
  descripcion_corta: string;
  importe: number;
  importe_formateado: string;
  moneda_simbolo: string;
}

type AccountBalanceResponse = Promise<{
  data: {
    comitente: {
      numero: string;
      descripcion: string;
    };
    disponible_cliente: {
      Dólar: Currency;
      "Mep Dólar": Currency;
      Pesos: Currency;
    };
  };
  success: boolean;
}>;

export const getAccountBalance = async ({
  accountNumber,
  date,
}: {
  accountNumber: string;
  date?: string;
}) => {
  const filterDate = date
    ? dayjs(date).format("YYYYMMDD")
    : dayjs().format("YYYYMMDD");

  const response = await axiosInstance.get(
    `/gestion/${accountNumber}/disponible/${filterDate}`
  );
  return response.data as AccountBalanceResponse;
};

export interface AccountContactResponse {
  data: {
    cbu: {
      id: number;
      cbu: string;
      cbu_tipo_estado: {
        id: number;
        color: string;
        descripcion_publica:
          | "Aprobado"
          | "Pendiente Aprobación"
          | "Pendiente Eliminación"
          | "Rechazado"
          | "Eliminado";
      };
      comitente: {
        nombre: string;
        numero: number;
      };
      cuit_asociado: number;
      descripcion: string;
      moneda: {
        id: number;
        descripcion: string;
      };
      referencia: string;
      tipo_cuenta_bancaria: {
        descripcion: "Caja Ahorro" | "Cuenta Corriente" | "Billetera Virtual";
      };
    }[];
    pagination?: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
      total_pages: number;
    };
  };
  success: boolean;
}

export interface ExternalAccountResponse {
  data: {
    cuenta_exterior: {
      id: number;
      account_number: string;
      bank: string;
      bank_address: string;
      bank_aba: string;
      bank_swift: string;
      bank_reference: string;
      beneficiario: string;
      comitente: {
        nombre: string;
        numero: number;
      };
      cuenta_exterior_tipo_estado: {
        id: number;
        color: string;
        descripcion_publica:
          | "Aprobado"
          | "Pendiente Aprobación"
          | "Pendiente Eliminación"
          | "Rechazado"
          | "Eliminado";
      };
      intermediary_bank: string;
      intermediary_bank_address: string;
      intermediary_bank_aba: string;
      intermediary_bank_swift: string;
      intermediary_bank_reference: string;
      intermediary_beneficiario: string;
      requiere_banco_intermediario: number;
      moneda: {
        id: number;
        descripcion: string;
      };
    }[];
    pagination?: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
      total_pages: number;
    };
  };
  success: boolean;
}

export const getAccountContacts = async ({
  accountNumber,
  perPage,
  currentPage = 1,
}: {
  accountNumber: string;
  perPage?: number;
  currentPage?: number;
}) => {
  const endpoint = perPage
    ? `/gestion/${accountNumber}/cbu?perPage=${perPage}&currentPage=${currentPage}`
    : `/gestion/${accountNumber}/cbu/habilitados`;

  const response = await axiosInstance.get(endpoint);

  return response.data as Promise<AccountContactResponse>;
};

export const getExternalAccounts = async ({
  accountNumber,
  perPage,
  currentPage = 1,
}: {
  accountNumber: string;
  perPage?: number;
  currentPage?: number;
}) => {
  const endpoint = perPage
    ? `/gestion/${accountNumber}/external-account?perPage=${perPage}&currentPage=${currentPage}`
    : `/gestion/${accountNumber}/external-account/habilitados`;

  const response = await axiosInstance.get(endpoint);

  return response.data as Promise<ExternalAccountResponse>;
};

export interface RequiredFileResponse {
  code: number;
  success: boolean;
  message: string;
  data: {
    mostrar_bloque_instructivo_permanente: boolean;
  };
}

export const getRequiredFileInput = async ({
  accountID,
  section,
}: {
  accountID: string;
  section: "CBU" | "TRANSFER" | "EXTERNAL_ACCOUNT";
}) => {
  const endpoint = `/recursos/check-instructivo?accountID=${accountID}&section=${section}`;

  const response = await axiosInstance.get(endpoint);

  return response.data as Promise<RequiredFileResponse>;
};

export interface CurrencyResponse {
  data: {
    moneda: {
      id: number;
      descripcion: string;
      descripcion_corta: string;
      simbolo: number;
      codigo: "MEP_DOLAR_USD" | "PESO_AR" | "DOLAR_USD";
      simbolo_esco: string;
      estado: string;
    }[];
  };
  success: boolean;
}

export const getCurrency = async () => {
  const response = await axiosInstance.get("/recursos/monedas");
  return response.data as Promise<CurrencyResponse>;
};

export interface AccountTypeResponse {
  data: {
    tipo_cuenta_bancaria: {
      id: number;
      descripcion: "Caja Ahorro" | "Cuenta Corriente" | "Billetera Virtual";
      codigo: string;
      estado: number;
    }[];
  };
  success: boolean;
}

export const getAccountTypes = async () => {
  const response = await axiosInstance.get("/recursos/tipo-cuentas-bancarias");
  return response.data as Promise<AccountTypeResponse>;
};

export const createTransaction = async (body: {
  date: string;
  amount: number | string;
  currencyId: number | string;
  cuit: number | string;
  cbuId?: number;
  accountNumber: number | string;
  cuentaExteriorId?: number | string;
  description?: string;
}) => {
  let payload = {
    fecha_transferencia: body.date,
    importe: Number(body.amount),
    moneda_id: Number(body.currencyId),
    cuit_asociado: Number(body.cuit),
    cbu_id: body.cbuId,
    transferencia_tipo_id: 1,
    numero_comitente: body.accountNumber,
    check_instructivo: 0,
    cuenta_exterior_id: body.cuentaExteriorId,
    observacion: body.description,
  };

  if (body.currencyId == 3) {
    delete payload.cbu_id;
  } else {
    delete payload.cuenta_exterior_id;
  }

  const response = await axiosInstance.post(
    `gestion/${body.accountNumber}/transferencias`,
    payload
  );
  return response.data as Promise<{
    success: boolean;
    data: {
      transfencia: {
        id: number;
      };
    };
  }>;
};

export const createAccountContact = async (data: {
  cbu: string | number;
  cuit: string | number;
  currency: number | string;
  nickname: string;
  accountType: number | string;
  accountNumber: number | string;
  accountName: string;
  file?: {
    base64: string;
    name: string;
  } | null;
}) => {
  let body: { [key: string]: number | string | object } = {
    cbu: data.cbu,
    cuit_asociado: data.cuit,
    moneda_id: Number(data.currency),
    referencia: data.nickname,
    tipo_cuenta_bancaria_id: Number(data.accountType),
    numero_comitente: data.accountNumber,
    nombre_comitente: data.accountName,
  };

  if (data.file) {
    body.archivo = data.file;
  }

  const response = await axiosInstance.post(
    `gestion/${data.accountNumber}/cbu`,
    body
  );
  return response.data as Promise<{
    success: boolean;
    data: {
      cbu: {
        id: number;
      };
      cuenta_exterior?: {
        id: number;
      };
    };
  }>;
};

export const createExternalBankAccount = async (data: {
  cuit: string | number;
  accountNumber: number | string;
  singleBank: string | number;
  singleBankNumber: string | number;
  singleBankSwift: string;
  singleBankABA: string;
  singleBankReference: string;
  isSingleBank: boolean;
  intermediaryBank: string | number;
  intermediaryBankSwift: string;
  intermediaryBankABA: string;
  intermediaryBankReference: string;
  observation?: string;
  file?: {
    base64: string;
    name: string;
  } | null;
}) => {
  let body: { [key: string]: number | string | object } = {
    numero_comitente: data.accountNumber,
    beneficiario: data.cuit,
    account_number: data.singleBankNumber,
    bank: data.singleBank,
    bank_aba: data.singleBankABA,
    bank_swift: data.singleBankSwift,
    bank_address: data.singleBankNumber,
    bank_reference: data.singleBankReference,
    referencia: data.singleBankReference,
    requiere_banco_intermediario: data.isSingleBank ? 0 : 1,
  };

  if (data.observation) {
    body.observacion = data.observation;
  }

  if (!data.isSingleBank) {
    body.intermediary_bank = data.intermediaryBank;
    body.intermediary_bank_aba = data.intermediaryBankABA;
    body.intermediary_bank_swift = data.intermediaryBankSwift;
    body.intermediary_bank_reference = data.intermediaryBankReference;
  }

  if (data.file) {
    body.archivo = data.file;
  }

  const response = await axiosInstance.post(
    `gestion/${data.accountNumber}/external-account`,
    body
  );
  return response.data as Promise<{
    success: boolean;
    data: {
      cbu?: {
        id: number;
      };
      cuenta_exterior: {
        id: number;
      };
    };
  }>;
};

export interface AccountCuitResponse {
  data: {
    cuit_asociado: string[];
  };
  success: boolean;
}
export const getAccountCuits = async (accountNumber: string) => {
  const response = await axiosInstance.get(
    `/gestion/${accountNumber}/cuits-asociados`
  );
  return response.data as Promise<AccountCuitResponse>;
};

// beneficiary accounts
export interface BeneficiaryAccountsResponse {
  data: {
    beneficiarios: {
      cuit_comitente: string;
    }[];
  };
  success: boolean;
}

export const getBeneficiaryAccounts = async (accountNumber: string) => {
  const response = await axiosInstance.get(
    `/gestion/${accountNumber}/external-account/beneficiarios`
  );
  return response.data as Promise<BeneficiaryAccountsResponse>;
};

export const deleteAccountContact = async ({
  contactId,
  accountNumber,
}: {
  contactId: number;
  accountNumber: number | string;
}) => {
  const response = await axiosInstance.delete(
    `gestion/${accountNumber}/cbu/${contactId}`,
    {
      data: {},
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
  return response.data as Promise<{
    success: boolean;
    code?: number;
    data:
      | string
      | {
          cbu: {
            id: number;
          };
        };
  }>;
};

export const deleteExternalAccount = async ({
  contactId,
  accountNumber,
}: {
  contactId: number;
  accountNumber: number | string;
}) => {
  const response = await axiosInstance.delete(
    `gestion/${accountNumber}/external-account/${contactId}`,
    {
      data: {},
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
  return response.data as Promise<{
    success: boolean;
    data: {
      cuenta_exterior: {
        id: number;
      };
    };
  }>;
};

export const createEcheq = async (data: {
  date: string;
  amount: number | string;
  cuit: number | string;
  accountNumber: number | string;
  file?: {
    base64: string;
    name: string;
  } | null;
  currencyID: number;
}) => {
  let body: { [key: string]: number | string | object } = {
    check_instructivo: 0,
    cuit_asociado: Number(data.cuit),
    fecha_transferencia: data.date,
    importe: Number(data.amount),
    moneda_id: data.currencyID,
    numero_comitente: data.accountNumber,
    transferencia_tipo_id: 2,
  };

  if (data.file) {
    body.archivo = data.file;
  }

  const response = await axiosInstance.post(
    `gestion/${data.accountNumber}/transferencias`,
    body
  );
  return response.data as Promise<{
    success: boolean;
    data: {
      transfencia: {
        id: number;
      };
    };
  }>;
};

export interface ApiErrorGeneric {
  response?: {
    data?: {
      message?: string;
      data?:
        | string
        | {
            error: {
              [key: string]: string[];
            };
          };
    };
  };
}

export const getApiErrorMessage = (error: ApiErrorGeneric) => {
  const data = error?.response?.data;
  const errorData = (data?.data as { error: { [key: string]: string[] } })
    ?.error;
  const specificError = errorData ? Object.values(errorData)[0]?.[0] : null;

  return specificError || data?.message || genericErrorMessage;
};
